import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { http } from "../lib/api";
import { cx } from "../lib/format";

function Stars({ value, size = 14, onSelect }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} onClick={onSelect ? () => onSelect(i) : undefined}
          data-testid={onSelect ? `star-${i}` : undefined}
          className={cx(onSelect && "cursor-pointer", i <= Math.round(value) ? "fill-current text-amber-400" : "text-zinc-600")} />
      ))}
    </div>
  );
}

export default function ProductReviews({ slug, productId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", text: "", customer_name: "", email: "" });

  const load = () => http.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text || !form.customer_name || !form.email) { toast.error("Please fill all fields"); return; }
    try {
      const r = await http.post("/reviews", { ...form, product_id: productId });
      toast.success(r.data.message || "Review submitted");
      setOpen(false);
      setForm({ rating: 5, title: "", text: "", customer_name: "", email: "" });
    } catch { toast.error("Could not submit review"); }
  };

  if (!data) return null;
  const { summary, reviews } = data;
  const maxD = Math.max(...Object.values(summary.distribution || {}), 1);

  return (
    <section className="mt-16 border-t border-zinc-800 pt-12" data-testid="product-reviews">
      <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-wide mb-6">Reviews</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <span className="font-display text-6xl text-white leading-none">{summary.avg || 0}</span>
            <div className="pb-1"><Stars value={summary.avg} size={16} /><p className="text-xs text-zinc-500 mt-1">{summary.count} reviews</p></div>
          </div>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="flex items-center gap-2 text-xs text-zinc-400">
                <span className="w-3">{n}</span><Star size={11} className="fill-current text-amber-400" />
                <div className="flex-1 h-1.5 bg-zinc-900"><div className="h-full bg-amber-400" style={{ width: `${((summary.distribution?.[n] || 0) / maxD) * 100}%` }} /></div>
                <span className="w-6 text-right">{summary.distribution?.[n] || 0}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setOpen((o) => !o)} className="btn-accent px-5 py-3 text-xs font-bold uppercase tracking-wider" data-testid="write-review-btn">Write a Review</button>
        </div>

        <div className="lg:col-span-2 space-y-5">
          {open && (
            <form onSubmit={submit} className="bg-zinc-900/50 border border-zinc-800 p-5 space-y-3" data-testid="review-form">
              <Stars value={form.rating} size={22} onSelect={(r) => setForm({ ...form, rating: r })} />
              <input placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm" data-testid="review-title" />
              <textarea placeholder="Share your thoughts…" rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm" data-testid="review-text" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Your name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm" data-testid="review-name" />
                <input type="email" placeholder="Email (private)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm" data-testid="review-email" />
              </div>
              <button className="btn-accent px-5 py-2.5 text-xs font-bold uppercase tracking-wider" data-testid="review-submit">Submit Review</button>
            </form>
          )}
          {reviews.length === 0 ? (
            <p className="text-zinc-500 text-sm">No reviews yet. Be the first to review this piece.</p>
          ) : reviews.map((r) => (
            <div key={r.id} className="border-b border-zinc-900 pb-4">
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                {r.verified_buyer && <span className="text-[10px] uppercase tracking-wider text-accent border border-accent-ss px-1.5" style={{ borderColor: "var(--ss-accent)" }}>Verified Buyer</span>}
              </div>
              {r.title && <p className="text-sm font-semibold text-white mt-2">{r.title}</p>}
              <p className="text-sm text-zinc-400 mt-1">{r.text}</p>
              <p className="text-[11px] text-zinc-600 mt-2">{r.customer_name} · {(r.created_at || "").slice(0, 10)}</p>
              {r.reply && <p className="text-xs text-zinc-400 mt-2 pl-3 border-l-2 border-accent-ss"><span className="text-accent font-semibold">StreetSins:</span> {r.reply}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
